// 評判システム（v0.5.1）。「客がいない時間があるのはつまらない。序盤まちまち→
// 徐々に行列」というユーザー要望に応えるための、累計実績 → 来店テンポの成長曲線。
//
// 設計のねらい（数値バランスの意図）:
//  - **序盤からそこそこ来る**: 評判 0 でも arrivalMultiplier は 1.0（基礎レート側を
//    底上げしてあるので、プレイ開始直後でも数十秒に 1 体は来る）。
//  - **やるほど賑わう**: 累計来店と図鑑発見で評判が上がり、来店倍率が乗算的に増える。
//    上限を設けて青天井のインフレは避ける（cozy 放置の方針）。
//  - **同時滞在の上限も評判で伸びる**: 序盤は 3 体程度、育つと最大 10 体。上限に達したら
//    描画側がドア前に行列を作る（このモジュールは上限値だけを決め、並ばせ方は render）。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。状態は GameState（totalVisits・dex）
// が持ち、ここはそこから派生量を計算するだけ。テストで決定性・単調性を担保する。

import type { GameState } from './state';

/** 同時滞在の下限（評判 0 の序盤）と上限（育ちきり）。 */
export const MIN_CONCURRENT = 3;
export const MAX_CONCURRENT = 10;

/** 来店倍率の上限（基礎レート × この値が最大の賑わい）。 */
export const MAX_ARRIVAL_MULTIPLIER = 3.0;

/** 図鑑 1 種の発見に与える評判ボーナス（来店数換算）。新種開拓を促す。 */
const DISCOVERY_BONUS = 8;

/**
 * 評判値を算出する純関数。累計来店数に、図鑑で発見済みの客種ぶんのボーナスを足す。
 *
 * 来店を重ねるほど・新しい客に出会うほど評判が上がる。発見ボーナスを大きめに取り、
 * 「庭を変えて新しい客を呼ぶ」行為に報いる（GAME_DESIGN のコアループの後押し）。
 */
export function reputation(state: GameState): number {
  let discovered = 0;
  for (const e of Object.values(state.dex)) if (e.discovered) discovered++;
  return Math.max(0, state.totalVisits) + discovered * DISCOVERY_BONUS;
}

/**
 * 評判 → 来店倍率（基礎レートに乗じる係数）。1.0 から始まり、評判とともに
 * 逓減しながら MAX_ARRIVAL_MULTIPLIER に漸近する（飽和曲線。序盤の伸びを実感しやすく、
 * 終盤は緩やかにして頭打ちにする）。
 *
 * 曲線: mult = 1 + (MAX-1) * rep / (rep + HALF)。rep=HALF で中間点。
 * HALF=60 → だいたい「累計 60 来店 + 数種発見」あたりで体感が半分まで伸びる。
 */
export function arrivalMultiplier(rep: number): number {
  const HALF = 60;
  const span = MAX_ARRIVAL_MULTIPLIER - 1;
  return 1 + (span * rep) / (rep + HALF);
}

/**
 * 評判 → 同時滞在の上限。MIN_CONCURRENT から MAX_CONCURRENT へ、評判とともに
 * 段階的に増える（飽和曲線を四捨五入で量子化）。上限に達した客はドア前で行列待ち
 * （render が表現）。
 *
 * 曲線: cap = MIN + (MAX-MIN) * rep / (rep + HALF)。HALF=80 で中盤に最大付近へ。
 */
export function maxConcurrent(rep: number): number {
  const HALF = 80;
  const span = MAX_CONCURRENT - MIN_CONCURRENT;
  const raw = MIN_CONCURRENT + (span * rep) / (rep + HALF);
  return Math.round(raw);
}

/** GameState から直接、来店倍率を得る近道。 */
export function arrivalMultiplierFor(state: GameState): number {
  return arrivalMultiplier(reputation(state));
}

/** GameState から直接、同時滞在上限を得る近道（render の行列判断に使う）。 */
export function maxConcurrentFor(state: GameState): number {
  return maxConcurrent(reputation(state));
}
